// @/hooks/useContentInvitation.js
import { useStaticQuery, graphql } from 'gatsby';
import { useLanguage } from '@/components/providers/LanguageContext';
import { useState, useEffect } from 'react';
import { contentStore } from '@/services/app/content';

/**
 * Hook to fetch Invitation & Auth content from Gatsby's GraphQL layer
 * Uses language context to automatically update content when language changes
 *
 * @param {string} [fallbackLocale='en'] - Fallback locale if context not available or locale not found
 * @returns {Object|null} Content for requested locale or null if no content exists at all
 */
export const useContentInvitation = (fallbackLocale = 'en') => {
  // Get current locale from context or use fallback
  const { locale } = useLanguage?.() || { locale: fallbackLocale };

  // Execute the GraphQL query (available immediately from static data)
  const data = useStaticQuery(graphql`
    query {
      allContentInvitation {
        nodes {
          id
          # Process Messages
          processingInvitationMessage
          checkingAuthMessage
          redirectingLoginMessage
          savingInvitationMessage
          
          # Invitation Card Content
          invitationTitle
          invitationDetailsIntro
          invitationSupportText
          invitationJoinStatement
          invitationMoreReasons
          
          # Account Status Messages
          accountExistsMessage
          accountExistsGuidance
          accountNotExistsMessage
          accountRegisteredTitle
          accountRegisteredMessage
          accountRegisteredSuffix
          emailVerificationTitle
          emailVerificationMessage
          emailCheckMessage
          
          # Token Status
          tokenInvalidTitle
          tokenInvalidSubtitle
          tokenInvalidMessage
          tokenInvalidGuidance
          tokenExpiredMessage
          
          # Auth Components
          logoutLoadingMessage
          logoutCompleteTitle
          logoutCompleteMessage
          platformAboutLabel
          platformPrivacyLabel
          platformTermsLabel
          logoutInactivityTitle
          logoutInactivityMessage
          
          # Error Components
          errorTitle
          errorSubtitle
          errorDefaultMessage
          errorNetworkTitle
          errorNetworkMessage
          errorServerTitle
          errorPermissionTitle
          errorNotFoundTitle
          errorGuidance
          
          # Message Types
          messageInfoTitle
          messageInfoSubtitle
          messageSuccessTitle
          messageWarningTitle
          messageWarningSubtitle
          
          # Button Labels
          buttonGetHelp
          buttonLogin
          buttonSignInAgain
          buttonRegisterNew
          buttonLoginExisting
          buttonRetry
          buttonGoHome
          buttonContactSupport
          buttonCancel

          preLoginLoadingMessage
          postLoginLoadingMessage
          
          locale
        }
      }
    }
  `);

  // Helper function to compute content from GraphQL data
  // This can be called synchronously for initial state
  const computeContent = (targetLocale) => {
    const nodes = data.allContentInvitation?.nodes || [];

    // First try the requested locale
    const localeContent = nodes.find(node => node.locale === targetLocale);

    // Fall back to English if the requested locale isn't available
    const englishContent = targetLocale !== 'en' ? nodes.find(node => node.locale === 'en') : null;

    // Check if we have GraphQL content
    if (localeContent || englishContent) {
      return localeContent || englishContent || null;
    }

    // Try to get content from the store as a fallback
    try {
      const storeContent = contentStore.getInvitationContent(targetLocale);
      if (storeContent) {
        return storeContent;
      }
    } catch (err) {
      // Silent error handling
    }

    return null;
  };

  // Initialize state with computed content for current locale
  const [content, setContent] = useState(() => computeContent(locale));

  // Update content when locale changes
  useEffect(() => {
    const newContent = computeContent(locale);
    setContent(newContent);
  }, [locale, data.allContentInvitation?.nodes]);

  // Register effect for content change events - ADDED for language integration
  useEffect(() => {
    const handleContentRefresh = (event) => {
      if (event.detail?.locale) {
        // Force refresh content when an external event requests it
        try {
          const storeContent = contentStore.getInvitationContent(event.detail.locale);
          if (storeContent) {
            setContent(storeContent);
          }
        } catch (err) {
          // Silent error handling
        }
      }
    };

    // Listen for content refresh events
    window.addEventListener('forceContentRefresh', handleContentRefresh);
    window.addEventListener('forceInvitationRefresh', handleContentRefresh);

    // Clean up listeners
    return () => {
      window.removeEventListener('forceContentRefresh', handleContentRefresh);
      window.removeEventListener('forceInvitationRefresh', handleContentRefresh);
    };
  }, []);

  return content;
};

/**
 * Fallback version for components that don't have access to the language context
 * @param {string} locale - Desired locale (defaults to 'en')
 * @returns {Object|null} Content for requested locale or null
 */
export const getContentInvitationForLocale = (locale = 'en') => {
  // First try to get content from the store
  try {
    const storeContent = contentStore.getInvitationContent(locale);
    if (storeContent) {
      return storeContent;
    }
  } catch (err) {
    // Silent error handling
  }

  // Fall back to Gatsby data if store fails
  try {
    if (typeof window !== 'undefined' && window.__GATSBY_INITIAL_DATA__) {
      // Attempt to access GraphQL data from Gatsby
      const staticQueries = window.__GATSBY_INITIAL_DATA__.staticQueryResults || {};

      for (const queryId in staticQueries) {
        const queryResult = staticQueries[queryId];
        const nodes = queryResult?.data?.allContentInvitation?.nodes || [];

        if (nodes.length > 0) {
          // First try the requested locale
          const localeContent = nodes.find(node => node.locale === locale);

          // Fall back to English if the requested locale isn't available
          const englishContent = locale !== 'en' ? nodes.find(node => node.locale === 'en') : null;

          return localeContent || englishContent || null;
        }
      }
    }
  } catch (error) {
    // Silent error handling
  }

  return null;
};